const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

// ── Survey list ───────────────────────────────────────────────────────────────

export interface SurveyListItem {
  id: string;
  title: string;
  isActive: boolean;
  isImported: boolean;
  importLabel: string | null;
  startsAt: string | null;
  closedAt: string | null;
  pendingReminders: number;
}

export async function fetchSurveys(accessToken: string): Promise<SurveyListItem[]> {
  const res = await fetch(`${BASE}/api/surveys`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('fetch_surveys_failed');
  return res.json();
}

// ── Reminder management ───────────────────────────────────────────────────────

export interface ReminderDto {
  id: string;
  scheduledAt: string;
  sentAt: string | null;
  isCancelled: boolean;
}

export async function fetchReminders(
  surveyId: string,
  accessToken: string
): Promise<ReminderDto[]> {
  const res = await fetch(`${BASE}/api/surveys/${surveyId}/reminders`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('fetch_reminders_failed');
  return res.json();
}

export async function rescheduleReminder(
  surveyId: string,
  reminderId: string,
  scheduledAt: string,
  accessToken: string
): Promise<ReminderDto> {
  const res = await fetch(`${BASE}/api/surveys/${surveyId}/reminders/${reminderId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ scheduledAt }),
  });
  if (!res.ok) throw new Error('reschedule_failed');
  return res.json();
}

export async function cancelReminder(
  surveyId: string,
  reminderId: string,
  accessToken: string
): Promise<void> {
  const res = await fetch(`${BASE}/api/surveys/${surveyId}/reminders/${reminderId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('cancel_failed');
}

// ── Survey creation ───────────────────────────────────────────────────────────

export interface SurveyQuestionItem {
  id: string;
  text: string;
  type: 'Scale' | 'MultipleChoice' | 'FreeText';
  displayOrder: number;
}

export interface CreateSurveyPayload {
  title: string;
  description?: string;
  invitationMessage?: string;
  landingPageMessage?: string;
  closedAt: string;
  copyQuestionsFromSurveyId?: string;
  questions?: { text: string; type: string; displayOrder: number }[];
}

export async function fetchSurveyQuestions(
  surveyId: string,
  accessToken: string
): Promise<SurveyQuestionItem[]> {
  const res = await fetch(`${BASE}/api/surveys/${surveyId}/questions`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('fetch_questions_failed');
  return res.json();
}

export async function createSurvey(
  body: CreateSurveyPayload,
  accessToken: string
): Promise<{ id: string; title: string; questionCount: number }> {
  const res = await fetch(`${BASE}/api/surveys`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('create_survey_failed');
  return res.json();
}

export interface QuestionDto {
  questionId: string;
  text: string;
  type: 'Scale' | 'MultipleChoice' | 'FreeText';
  displayOrder: number;
}

export interface SurveyDto {
  surveyId: string;
  title: string;
  description: string;
  questions: QuestionDto[];
  landingPageMessage: string;
  expiresAt: string | null;
}

export interface AnswerDto {
  questionId: string;
  value: string;
}

export interface TeamResponseRateDto {
  teamLabel: string;
  respondedCount: number;
  totalInvited: number;
  belowAnonymityThreshold: boolean;
}

export interface SurveyResponseRateReport {
  surveyId: string;
  surveyTitle: string;
  anonymityThreshold: number;
  teams: TeamResponseRateDto[];
}

export async function fetchSurveyByToken(token: string): Promise<SurveyDto> {
  const res = await fetch(`${BASE}/api/s/${token}`);
  if (!res.ok) throw new Error('invalid_token');
  return res.json();
}

export async function submitSurvey(token: string, answers: AnswerDto[]): Promise<void> {
  const res = await fetch(`${BASE}/api/s/${token}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
  if (!res.ok) throw new Error('submit_failed');
}

export async function fetchResponseRates(
  surveyId: string,
  accessToken: string
): Promise<SurveyResponseRateReport> {
  const res = await fetch(`${BASE}/api/surveys/${surveyId}/response-rates`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('fetch_rates_failed');
  return res.json();
}
